import {useRef, useCallback} from 'react';
import {useTextField} from '@react-aria/textfield';
import classnames from 'classnames';
import {useIntl} from 'react-intl';

import {TypographySize, useTypography} from '../../../hooks/typography';
import {Tag} from '../../../atoms/tag';

import type {Props} from './types';
import styles from './styles.module.css';

export const AutocompleteInput = <Chip, >({
    chips,
    getKeyFromChip,
    getLabelFromChip,
    getIconFromChip,
    query,
    onChange,
    onChipRemove,
    textFieldProps,
    errorMessage = '',
    noWrapChips = false,
}: Props<Chip>) => {
    const {formatMessage} = useIntl();
    const handleInputChange = useCallback(
        (nextQuery: string) => {
            onChange(nextQuery);
        },
        [onChange],
    );
    const inputRef = useRef<HTMLInputElement>(null);
    const {inputProps, errorMessageProps} = useTextField(
        {
            'aria-label': formatMessage({
                id: 'autocomplete.input',
                defaultMessage: 'Autocomplete Input',
            }),
            ...textFieldProps,
            onChange: handleInputChange,
            value: query,
        },
        inputRef,
    );

    const [inputTypographyCls, errorMessageTypographyCls] = useTypography([
        {
            size: TypographySize.BodyL,
            noDefaultColor: true,
        },
        {
            size: TypographySize.BodyS,
            noDefaultColor: true,
        },
    ]);

    return (
        <div>
            <div
                className={classnames(styles.wrapper, {
                    [styles.wrapperWithError]: Boolean(errorMessage),
                })}
            >
                <div className={styles.chipsContainer}>
                    <div className={classnames(styles.chips, {[styles.chipsNoWrap]: noWrapChips})}>
                        {chips.map((chip) => {
                            const chipKey = getKeyFromChip(chip);

                            return (
                                <div className={styles.chip} key={chipKey}>
                                    <Tag
                                        icon={getIconFromChip && getIconFromChip(chip)}
                                        id={chipKey}
                                        label={getLabelFromChip(chip)}
                                        onClose={() => onChipRemove(chipKey)}
                                    />
                                </div>
                            );
                        })}
                        {!noWrapChips && <input {...inputProps} className={classnames(styles.input, inputTypographyCls)} />}
                    </div>
                </div>
                {noWrapChips && (
                    <input {...inputProps} className={classnames(styles.input, styles.inputStandAlone, inputTypographyCls)} />
                )}
            </div>
            {Boolean(errorMessage) && (
                <p className={classnames(errorMessageTypographyCls, styles.errorMessage)} {...errorMessageProps}>
                    {errorMessage}
                </p>
            )}
        </div>
    );
};
